import { ILeadIntentOption } from '@bridebook/models/source/models/Suppliers/Leads.types';
import { ValidationError } from '@bridebook/toolbox/src';
import type { EnquiryIntents } from '@bridebook/web/lib/enquiries/intent';
import SendEnquiryError from '@bridebook/web/lib/enquiries/errors/send-enquiry-error';

export type EnquirySendAnalyticsOptions = {
  defaultIntentsSelected?: boolean;
  defaultMessageTyped?: boolean;
};

const KNOWN_FAILURE_REASONS = new Set([
  'ENQUIRY_SPAM_DETECTED',
  'FAILED_TO_SEND_ENQUIRY',
  'VALIDATION_ERROR',
  'NO_USER',
  'NO_SUPPLIER',
  'CAPTCHA_NOT_ENABLED',
  'TIMEOUT',
  'UNKNOWN',
]);

/** Compare intent selections; when `pillKeys` is set, only those keys are compared (LIVE-27666 pills). */
export const areEnquiryIntentsEqualForKeys = (
  initial: EnquiryIntents,
  submitted: EnquiryIntents,
  keys: readonly (keyof EnquiryIntents)[],
): boolean => keys.every((key) => Boolean(initial[key]) === Boolean(submitted[key]));

/** `true` when the couple sent with the same intent selection as the form's initial defaults. */
export const getDefaultIntentsSelected = ({
  initialIntent,
  submittedIntent,
  pillKeys,
}: {
  initialIntent: EnquiryIntents;
  submittedIntent: EnquiryIntents;
  pillKeys?: readonly ILeadIntentOption[];
}): boolean => {
  const keys = pillKeys ?? (Object.values(ILeadIntentOption) as readonly (keyof EnquiryIntents)[]);
  return areEnquiryIntentsEqualForKeys(initialIntent, submittedIntent, keys);
};

/** `true` when the couple sent with the default message (did not edit). */
export const getDefaultMessageTyped = ({
  messageEditedByUser,
}: {
  messageEditedByUser: boolean;
}): boolean => !messageEditedByUser;

/** Stable English codes for `failedToSendEnquiryReason` — never translated UI strings. */
export const getFailedToSendEnquiryReason = (error: unknown): string => {
  if (error instanceof Error) {
    if (KNOWN_FAILURE_REASONS.has(error.message)) {
      return error.message;
    }

    if (error instanceof ValidationError) {
      return 'VALIDATION_ERROR';
    }

    if (error instanceof SendEnquiryError) {
      return 'FAILED_TO_SEND_ENQUIRY';
    }

    switch (error.message) {
      case 'no user':
        return 'NO_USER';
      case 'no supplier':
      case 'Supplier is required to send an enquiry':
        return 'NO_SUPPLIER';
      case 'Captcha not enabled':
        return 'CAPTCHA_NOT_ENABLED';
      case 'Failed to send enquiry':
        return 'FAILED_TO_SEND_ENQUIRY';
      default:
        break;
    }
  }

  return 'UNKNOWN';
};
